import { FC, useEffect, useState } from 'react';
import { ConfirmationDialog, FormTextField } from '../../../../../lib/components';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Box, Grid, GridProps, Skeleton, Typography } from '@mui/material';
import { usePersonioAppDelete } from '../../../../../queries/company/personio/mutations';
import { UseMutateAsyncFunction, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { CompanySettings, CreatePersonioAppRequest, Personio } from '@pxtra-client-lib-legacy/gen';
import { getPersonioDetailsQuery } from '../../../../../queries/company/personio';
import { useStore } from '../../../../../hooks/useStore';
import { Button } from '../../../../../lib/components/atomic/button';

type PersonioConfigProps = {
  createPersonioApp: UseMutateAsyncFunction<
    Personio,
    Error,
    CreatePersonioAppRequest & { company: string },
    unknown
  >;
};

export const PersonioConfig: FC<PersonioConfigProps> = ({ createPersonioApp }) => {
  const { t } = useTranslation();

  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? 'MISSING_ID';
  const { values, setFieldValue } = useFormikContext<{
    personio: CreatePersonioAppRequest;
    settings: CompanySettings;
  }>();
  const queryClient = useQueryClient();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // query to get the personio details
  // const { data: personioDetails, isPending: personioPending } = usePersonioDetails(companyId);
  const { data: personioDetails, isPending: personioPending } = getPersonioDetailsQuery(companyId);

  // mutation to delete the personio details
  const { mutateAsync: deletePersonioIntegration } = usePersonioAppDelete({
    onSuccess: () => {
      enqueueSnackbar(t('company:PERSONIO_CONNECTION_DELETED'), {
        variant: 'success'
      });
      setFieldValue('personio.client_id', '');
      setFieldValue('personio.client_secret', '');
      queryClient.invalidateQueries({ queryKey: ['company', companyId, 'personio'] });
      queryClient.invalidateQueries({ queryKey: ['company', companyId, 'personio', 'check'] });
    },
    onError: () => {
      enqueueSnackbar(t('company:PERSONIO_CONNECTION_DELETION_FAILED'), {
        variant: 'error'
      });
    }
  });

  const personioAppAlreadyExists = personioDetails?.length;
  const personioClientSecretField = !personioAppAlreadyExists ? (
    <FormTextField
      label={t('company:PERSONIO_CLIENT_SECRET')}
      xs={3}
      type="password"
      name="personio.client_secret"
    />
  ) : null;
  const canTest = values.personio.client_id && values.personio.client_secret;

  const onTestClick = async () => {
    if (canTest) {
      await createPersonioApp({
        client_id: values.personio.client_id,
        client_secret: values.personio.client_secret,
        company: companyId
      });
    }
  };

  const handleDeleteIntegration = async () => {
    await deletePersonioIntegration({ company: companyId });
    setOpenDeleteDialog(false);
  };

  useEffect(() => {
    if (!personioPending && personioAppAlreadyExists) {
      setFieldValue('personio.client_id', personioDetails[0].client_id);
    }
  }, [personioDetails, personioPending, personioAppAlreadyExists]);

  return (
    <>
      {personioPending ? (
        <LoadingFormField label={t('company:PERSONIO_CLIENT_ID')} gridProps={{ xs: 3 }} />
      ) : (
        <FormTextField
          disabled={!!personioAppAlreadyExists}
          label={t('company:PERSONIO_CLIENT_ID')}
          xs={3}
          name="personio.client_id"
        />
      )}
      {personioPending ? (
        <LoadingFormField label={t('company:PERSONIO_CLIENT_ID')} gridProps={{ xs: 3 }} />
      ) : (
        personioClientSecretField
      )}
      <Grid
        item
        xs={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          paddingBottom: '2px'
        }}
      >
        {personioAppAlreadyExists ? (
          <Button color="error" onClick={() => setOpenDeleteDialog(true)}>
            {t('common:DELETE')}
          </Button>
        ) : (
          <Button type="button" variant="secondary" disabled={!canTest} onClick={onTestClick}>
            {t('common:TEST')}
          </Button>
        )}
      </Grid>
      <ConfirmationDialog
        variant="warning"
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteIntegration}
        title={t('company:PERSONIO_DELETE_DIALOG_TITLE')}
        contentText={t('company:PERSONIO_DELETE_DIALOG_TEXT')}
        confirmButtonText={t('common:DELETE')}
      />
    </>
  );
};

const LoadingFormField: FC<{
  label: string;
  gridProps: Omit<GridProps, 'item' | 'children' | 'sx'>;
}> = ({ label, gridProps }) => {
  return (
    <Grid item sx={{ display: 'flex', flexDirection: 'column' }} {...gridProps}>
      <Box>
        <Typography variant="body2" color="grey.900" marginBottom="0.5rem">
          {label}
        </Typography>
      </Box>
      <Skeleton variant="rounded" width="100%" height="100%" sx={{ borderRadius: '0.75rem' }} />
    </Grid>
  );
};
